import {Injectable} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';
import {ModalItemModel} from './modal-item.model';
import {CurrentPasswordComponent} from './components/current-password/current-password.component';
import {AddVehicleComponent} from './components/add-vehicle/add-vehicle.component';
import {RemoveComponent} from './components/remove/remove.component';
import {ChangeStatusComponent} from './components/change-status/change-status.component';
import {UntypedFormGroup} from '@angular/forms';
import {ModalState} from './modal-state.enum';
import {ModalComponent} from './modal.component';
import {PostApiModel} from '../../api/models/post-api.model';
import {MediaObjectViewModel} from '../../core/models/media-object.view-model';
import {environment} from '../../../environments/environment';
import {ModalStateEvent} from './modal-state-event';
import {ProjectViewModel} from '../../features/user/garage/project.view-model';
import {TranslateService} from '@ngx-translate/core';
import {AddGoalComponent} from './components/add-goal/add-goal.component';
import {AddCostComponent} from './components/add-cost/add-cost.component';
import {ProjectDescriptionComponent} from './components/project-description/project-description.component';
import {DescriptionPostViewModel} from '../../features/user/project/about/details/description-post.view-model';
import {HistoryPostViewModel} from '../../features/user/project/about/history-post.view-model';
import {HistoryPostComponent} from './components/history-post/history-post.component';
import {GoalViewModel} from '../../features/user/project/goals/goal.view-model';
import {CostViewModel} from '../../features/user/project/goals/cost.view-model';
import {AddMediaComponent} from './components/add-media/add-media.component';
import {AddEventComponent} from './components/add-event/add-event.component';
import {CurrentUserViewModel} from '../../core/models/current-user.view-model';
import {ParticipantApiModel} from '../../api/models/participant.api.model';
import {ParticipationListComponent} from './components/participation-list/participation-list.component';
import {EventApiModel} from '../../api/models/event.api.model';
import {OverlayGalleryComponent} from './components/overlay-gallery/overlay-gallery.component';
import {OverlayGalleryModalData} from './components/overlay-gallery/overlay-gallery-modal.interface';
import {PostViewModel} from '../timeline/post.view-model';
import {MediaViewComponent} from '../media-view/media-view.component';
import {CustomCameraMediaItem} from "../../features/custom-camera/custom-camera";
import {TimelineComponent} from "../timeline/timeline.component";
import {SearchFiltersComponent} from "./components/search-filters/search-filters.component";
import {ResultsListTabInterface} from "../../core/components/search-results-list/results-list-tab.interface";
import {
  AddVehicleAdvertisementComponent
} from "./components/add-vehicle-advertisement/add-vehicle-advertisement.component";
import {MediaChoiceComponent} from "./components/media-choice/media-choice.component";
import {MarketplaceAdvertisementApiModel} from "../../api/models/marketplace-advertisement.api.model";
import {ChangeMainOwnerComponent} from "./components/change-main-owner/change-main-owner.component";
import {ProjectApiModel} from "../../api/models/project.api-model";
import {AddDocumentsComponent} from "./components/add-documents/add-documents.component";
import {DocumentApiModel} from "../../api/models/document.api-model";
import {AbstractApiModel} from "../../api/models/abstract.api.model";
import {ViolationComponent} from "./components/violation/violation.component";
import {BugReportComponent} from "./components/bug-report/bug-report.component";
import {BugReportErrorContext} from '../../api/models/bug-report-error-context.model';
import {ViolationTypeEnum} from "../../core/enums/violation-type.enum";
import {CollaborationCreateComponent} from "../../features/user/collaboration/collaboration-create.component";
import {CollaborationEditComponent} from "../../features/user/collaboration/collaboration-edit.component";
import {CollaborationMediaPostCreateComponent} from "../../features/user/collaboration/collaboration-media-post-create.component";
import {SocialListModalComponent} from './components/social-list/social-list-modal.component';
import {SocialListModalConfig} from './components/social-list/social-list-modal-config.interface';
import {SocialListAdapterRegistryService} from './components/social-list/social-list-adapter-registry.service';
import {PostComposerModalComponent} from './components/post-composer/post-composer-modal.component';
import {ChatShareModalComponent} from './components/chat-share/chat-share-modal.component';
import {PlaceFiltersComponent, PlaceFilterValues} from './components/place-filters/place-filters.component';
import {AddPlaceComponent} from './components/add-place/add-place.component';
import {PlaceCategoryApiModel} from '../../api/models/place-category.api-model';
import {PlaceApiModel} from '../../api/models/place.api-model';
import {WebVehicleWizardComponent} from '../../features/user/garage/web-vehicle-wizard.component';
import {FlutterShellService} from '../../core/mobile/flutter-shell.service';
import {FlutterSocialListType} from '../../core/mobile/flutter-shell.plugin';


@Injectable({providedIn: 'root'})
export class ModalService {
  get hasButtons(): boolean {
    return this.modalInstance.hasButtons;
  }

  set hasButtons(value: boolean) {
    this.modalInstance.hasButtons = value;
  }

  set name(value: string) {
    const instance = this._modalInstance;
    if (!instance) {
      return;
    }
    Promise.resolve().then(() => instance.updateName(value));
  }

  set isHeaderVisible(value: boolean) {
    this._modalInstance.isHeaderVisible = value;
  }


  get modalInstance(): ModalComponent {
    return this._modalInstance;
  }

  set modalInstance(value: ModalComponent) {
    this._modalInstance = value;
  }

  private _modalInstance: ModalComponent;
  private successSubject: Subject<ModalStateEvent>;
  public successState: Observable<ModalStateEvent>;
  private pendingSubject: Subject<ModalStateEvent>;
  public pendingState: Observable<ModalStateEvent>;
  private modalStateSubscription: Subscription | null = null;

  public content: any;
  public selectedComponent: ModalItemModel;
  public isVisible: boolean;

  public styles: {slideFrom: string, modalType: string, size: string, light?: boolean, mobileFullscreen?: boolean};
  public btnSuccessName: string;

  constructor(
    private translate: TranslateService,
    private readonly socialListAdapterRegistry: SocialListAdapterRegistryService,
    private readonly flutterShell: FlutterShellService,
  ) { }

  private show() {
    // A new modal supersedes any unresolved result stream from the previous
    // dynamic child. Completing it releases consumers such as Home's Reels
    // subscription even when the modal is replaced rather than closed by the
    // dialog animation.
    this.successSubject?.complete();
    this.pendingSubject?.complete();
    this.modalStateSubscription?.unsubscribe();
    this.modalStateSubscription = null;
    const successSubject = new Subject<ModalStateEvent>();
    const pendingSubject = new Subject<ModalStateEvent>();
    this.successSubject = successSubject;
    this.successState = successSubject.asObservable();
    this.pendingSubject = pendingSubject;
    this.pendingState = pendingSubject.asObservable();
    this._modalInstance.component = this.selectedComponent;
    this._modalInstance.show();
    this.isVisible = true;
    const subscription = this._modalInstance.internalState.subscribe({
      next: (modalState: ModalStateEvent) => {
        switch (modalState.status) {
          case ModalState.SUCCESS:
            subscription.unsubscribe();
            if (this.modalStateSubscription === subscription) {
              this.modalStateSubscription = null;
            }
            this.isVisible = false;
            this.isHeaderVisible = true;
            this._modalInstance.requestHide(true);
            this.emitSuccess(successSubject, modalState);
            break;
          case ModalState.PENDING:
            this.emitPending(pendingSubject, modalState);
            break;
        }
      },
      complete: () => {
        // A modal can be dismissed or replaced without emitting SUCCESS.
        // Consumers must still be released in that path.
        successSubject.complete();
        pendingSubject.complete();
        if (this.modalStateSubscription === subscription) {
          this.modalStateSubscription = null;
        }
      },
    });
    this.modalStateSubscription = subscription;
  }

  public close(): void {
    this.isHeaderVisible = true;
    this._modalInstance.requestHide();
  }

  public forceClose(): void {
    this.isHeaderVisible = true;
    this.isVisible = false;
    this._modalInstance?.requestHide(true);
  }

  public emitState(state: ModalState, data: any = null): void {
    this._modalInstance.modalStateEvent.status = state;
    this._modalInstance.modalStateEvent.data = data;
    this._modalInstance.emitState(this._modalInstance.modalStateEvent);
  }

  private emitSuccess(subject: Subject<ModalStateEvent>, modalState: ModalStateEvent) {
    subject.next(modalState);
    subject.complete();
  }

  private emitPending(subject: Subject<ModalStateEvent>, modalState: ModalStateEvent) {
    subject.next(modalState);
  }

  public showRemoveProjectModal(project: ProjectViewModel): ModalService {
    this.name = this.translate.instant('shared.modal.modal_service.remove_project.title', {vehicleName: project.vehicle.fullVehicleIdentity});
    this.content = {
      id: project['@id'],
      route: environment.apiUrl,
      confirmMessage: this.translate.instant('shared.modal.modal_service.remove_project.confirm', {vehicleName: project.vehicle.fullVehicleIdentity}),
      successMessage: this.translate.instant('shared.modal.modal_service.remove_project.success', {vehicleName: project.vehicle.fullVehicleIdentity}),
      errorMessage: this.translate.instant('shared.modal.modal_service.remove_project.error')
    };
    this.styles = {slideFrom: 'top', modalType: 'modal-lg modal-notify modal-danger', size: 'modal-fluid'};
    this.hasButtons = true;
    this.selectedComponent = new ModalItemModel(RemoveComponent, this.content, 'remove');
    this.show();
    return this;
  }

  public showRemoveTileModal(timeLineItem: PostApiModel) {
    this.name = 'Remove Post';
    this.content = {
      id: timeLineItem.id,
      route: 'api_time_line_items_delete_item',
      message: 'Are you sure to remove this post? This action permanently wipe all informations about your section'
    };
    this.selectedComponent = new ModalItemModel(RemoveComponent, this.content, 'remove');
    this.show();
  }

  public showRemoveImageModal(mediaObject: MediaObjectViewModel, title: string, element: string) {
    this.name = title;
    this.styles = {slideFrom: 'top', modalType: 'modal-sm modal-notify modal-danger', size: 'modal-fluid'};
    this.content = {
      id: mediaObject['@id'],
      route: environment.apiUrl,
      confirmMessage: this.translate.instant('shared.modal.modal_service.remove_image.confirm', {element}),
      successMessage: this.translate.instant('shared.modal.modal_service.remove_image.success', {element}),
      errorMessage: this.translate.instant('shared.modal.modal_service.remove_image.error', {element}),
    };
    this.selectedComponent = new ModalItemModel(RemoveComponent, this.content, 'remove');
    this.hasButtons = true;
    this.show();
    return this;
  }

  public showAddProjectModal(): ModalService {
    this.name = this.translate.instant('shared.modal.modal_service.add_project.title');
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.selectedComponent = new ModalItemModel(AddVehicleComponent, this.content, 'add-vehicle');
    this.show();
    return this;
  }

  /**
   * Responsive Angular vehicle wizard for browser/PWA and Capacitor fallback. It uses the shared
   * modal host so focus management, the close guard and mobile fullscreen
   * behaviour are identical to every other application modal.
   */
  public showWebVehicleWizardModal(data: {currentUser: CurrentUserViewModel}): ModalService {
    this.name = this.translate.instant('features.user.garage.mobile_wizard.catalog.vehicleType');
    this.styles = {
      slideFrom: 'top',
      // A reusable PrimeNG dialog mode. On phones it owns the entire visual
      // viewport; desktop retains the regular constrained modal.
      modalType: 'web-vehicle-wizard-modal modal-nogutter modal-mobile-fullscreen',
      size: 'modal-fluid',
      mobileFullscreen: true,
    };
    this.hasButtons = false;
    this.content = data;
    this.selectedComponent = new ModalItemModel(WebVehicleWizardComponent, this.content, 'web-vehicle-wizard');
    this.show();
    return this;
  }

  public showAddProjectCollaborationModal(data: {currentUserVM: CurrentUserViewModel, defaultProjectIri?: string | null}): ModalService {
    this.name = this.translate.instant('features.user.collaboration.create.title');
    this.styles = {
      slideFrom: 'top',
      modalType: 'modal-lg modal-notify modal-info no-padding-modal',
      size: 'modal-fluid',
    };
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('features.user.collaboration.create.submit');
    this.content = data;
    this.selectedComponent = new ModalItemModel(CollaborationCreateComponent, this.content, 'add-project-collaboration');
    this.show();
    return this;
  }

  public showEditProjectCollaborationModal(data: {collaboration: any}): ModalService {
    this.name = this.translate.instant('features.user.collaboration.detail.about_collaboration');
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('common.button.confirm_changes');
    this.content = data;
    this.selectedComponent = new ModalItemModel(CollaborationEditComponent, this.content, 'edit-project-collaboration');
    this.show();
    return this;
  }

  public showCreateCollaborationMediaPostModal(data: {collaboration: any, currentUser: CurrentUserViewModel}): ModalService {
    this.name = this.translate.instant('features.user.collaboration.detail.add_assets');
    this.styles = {slideFrom: 'top', modalType: 'modal-lg modal-notify modal-info no-padding-modal', size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('common.add_material');
    this.content = data;
    this.selectedComponent = new ModalItemModel(CollaborationMediaPostCreateComponent, this.content, 'create-collaboration-media-post');
    this.show();
    return this;
  }

  public showEditCollaborationMediaPostModal(data: {collaboration: any, currentUser: CurrentUserViewModel, post: PostViewModel}): ModalService {
    this.name = this.translate.instant('features.user.collaboration.detail.edit_media_post');
    this.styles = {slideFrom: 'top', modalType: 'modal-lg modal-notify modal-info no-padding-modal', size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('common.update');
    this.content = data;
    this.selectedComponent = new ModalItemModel(CollaborationMediaPostCreateComponent, this.content, 'edit-collaboration-media-post');
    this.show();
    return this;
  }

  public showProjectDescriptionModal(data: {projectVM: ProjectViewModel, descriptionPost?: DescriptionPostViewModel}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.project_description.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(ProjectDescriptionComponent, this.content, 'project-description');
    this.show();
    return this;
  }

  public showHistoricalEntryModal(data: {projectVM: ProjectViewModel, historyPost?: HistoryPostViewModel}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.history_entry.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(HistoryPostComponent, this.content, 'project-history');
    this.show();
    return this;
  }

  public showGoalModal(data: {projectVM: ProjectViewModel, goalVM?: GoalViewModel}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.add_goal.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddGoalComponent, this.content, 'add-goal');
    this.show();
    return this;
  }

  public showCostModal(data: {projectVM: ProjectViewModel, goalVMs: GoalViewModel[], costVM?: CostViewModel}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.add_cost.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddCostComponent, this.content, 'add-cost');
    this.show();
    return this;
  }

  public showMediaModal(data: {projectVM: ProjectViewModel, eventAM: EventApiModel, media: CustomCameraMediaItem, isImagePicked: boolean, timeline: TimelineComponent}): ModalService {
    this.isHeaderVisible = false;
    this.hasButtons = data.isImagePicked;
    this.name = this.translate.instant(`shared.modal.modal_service.add_media.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info app-media-modal no-padding-modal', size: 'modal-fluid'};
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddMediaComponent, this.content, 'add-media');
    this.show();
    return this;
  }

  public showEventModal(data: {eventAM: EventApiModel, userVM: CurrentUserViewModel}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.add_event.title`);
    this.styles = {
      slideFrom: 'top',
      modalType: 'modal-lg modal-notify modal-info no-padding-modal',
      size: 'modal-fluid',
    };
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddEventComponent, this.content, 'add-event');
    this.show();
    return this;
  }

  public showChangeProjectStatusModal(project: any): void {
    this.name = 'Change Vehicle project status';
    this.content = {
      item: project,
      route: 'api_projects_delete_item',
      message: null
    };
    this.styles = {slideFrom: 'top', modalType: 'modal-sm modal-notify modal-warning', size: 'modal-fluid'};
    this.hasButtons = true;
    this.selectedComponent = new ModalItemModel(ChangeStatusComponent, this.content, 'change-status');
    this.show();
  }

   public showAskForCredentialsModal(form: UntypedFormGroup): this {
    this.name = this.translate.instant('shared.modal.modal_service.current_password.title');
    this.content = form;
    this.styles = {slideFrom: 'top', modalType: 'modal-sm modal-top modal-notify modal-warning', size: 'modal-fluid'};
    this.hasButtons = true;
    this.selectedComponent = new ModalItemModel(CurrentPasswordComponent, this.content, 'current-password');
    this.show();
    return this;
  }

  public showSocialListModal(config: SocialListModalConfig): this {
    const listType = config.listType as FlutterSocialListType;
    const subjectIri = String(config.subject?.['@id'] || '');
    if (this.flutterShell.canPresentSocialList && subjectIri && this.isFlutterSocialListType(listType)) {
      void this.flutterShell.presentSocialList({listType, subjectIri}).then((outcome) => {
        if (outcome === null) {
          this.showWebSocialListModal(config);
        }
      });
      return this;
    }

    return this.showWebSocialListModal(config);
  }

  private showWebSocialListModal(config: SocialListModalConfig): this {
    const adapter = this.socialListAdapterRegistry.get(config.listType);
    this.name = this.translate.instant(config.titleKey || adapter.titleTranslationKey);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info social-list', size: 'modal-fluid'};
    this.content = config;
    this.hasButtons = false;
    this.selectedComponent = new ModalItemModel(SocialListModalComponent, this.content, 'social-list');
    this.show();
    return this;
  }

  private isFlutterSocialListType(value: string): value is FlutterSocialListType {
    return [
      'user-follows',
      'project-follows',
      'event-follows',
      'user-reactions',
      'project-reactions',
      'local-subject-reactions',
    ].includes(value);
  }

  public showFollowingsModal(config: SocialListModalConfig): this {
    return this.showSocialListModal(config);
  }

  public showReactionsModal(config: SocialListModalConfig): this {
    return this.showSocialListModal(config);
  }

  public showParticipationListModal(participantAMs: ParticipantApiModel[], state: ('TAKES_PART' | 'INTERESTED' | 'ORGANIZES')[]): this {
    this.name = this.translate.instant('shared.modal.modal_service.event.participants.title');
    this.styles = {slideFrom: 'top', modalType: 'modal-xs modal-notify modal-info', size: 'modal-fluid'};
    return this.participationModal({participantAMs, state});
  }

  public participationModal(data: {participantAMs: ParticipantApiModel[], state: ('TAKES_PART' | 'INTERESTED' | 'ORGANIZES')[]}) {
    this.content = data;
    this.hasButtons = false;
    this.selectedComponent = new ModalItemModel(ParticipationListComponent, this.content, 'participation-list');
    this.show();
    return this;
  }

  public showOverlayGalleryModal(data: OverlayGalleryModalData): ModalService {
    this.styles = {slideFrom: 'top', modalType: 'modal-notify modal-info p-dialog-maximized modal-maximized modal-nogutter overlay-gallery-modal', size: 'modal-fluid'};
    this.hasButtons = false;
    this.isHeaderVisible = false;
    this.content = data;
    this.selectedComponent = new ModalItemModel(OverlayGalleryComponent, this.content, 'overlay-gallery');
    this.show();
    return this;
  }

  public showPostComposerModal(data: {currentUserVM: CurrentUserViewModel, projectVM?: ProjectViewModel | null, eventAM?: EventApiModel | null, sharedPost?: PostViewModel | null, editingPost?: PostViewModel | null}): ModalService {
    this.name = this.translate.instant(data?.editingPost
      ? 'shared.share.composer.edit_title'
      : data?.sharedPost ? 'shared.share.composer.share_title' : 'shared.share.composer.post_title');
    this.styles = {slideFrom: 'top', modalType: 'modal-notify modal-info no-padding-modal post-composer-modal-dialog', size: 'modal-fluid'};
    this.hasButtons = false;
    this.content = data;
    this.selectedComponent = new ModalItemModel(PostComposerModalComponent, this.content, 'post-composer');
    this.show();
    return this;
  }

  public showChatShareModal(data: {currentUserVM: CurrentUserViewModel, sharedPost: PostViewModel}): ModalService {
    this.name = this.translate.instant('shared.share.chat.title');
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = false;
    this.content = data;
    this.selectedComponent = new ModalItemModel(ChatShareModalComponent, this.content, 'chat-share');
    this.show();
    return this;
  }

  public showSearchFiltersModal(
    data: {selectedTab: ResultsListTabInterface; searchQuery?: string; mobileFocusTopOffset?: number},
    modalSize: string = 'modal-md'
  ): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.search_filters.title`);
    this.styles = {slideFrom: 'top', modalType:  `${modalSize} modal-notify modal-info`, size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant(`shared.modal.modal_service.search_filters.apply`);
    this.content = data;
    this.selectedComponent = new ModalItemModel(SearchFiltersComponent, this.content, 'search-filters');
    this.show();
    return this;
  }

  public showPlaceFiltersModal(data: {filters: PlaceFilterValues; categories: PlaceCategoryApiModel[]}): ModalService {
    this.name = this.translate.instant('places.filters.title');
    this.styles = {slideFrom: 'top', modalType: 'modal-lg modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('places.filters.apply');
    this.content = data;
    this.selectedComponent = new ModalItemModel(PlaceFiltersComponent, this.content, 'place-filters');
    this.show();
    return this;
  }

  public showAddPlaceModal(data: {categories: PlaceCategoryApiModel[]}): ModalService {
    this.name = this.translate.instant('places.add.title');
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('places.add.submit');
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddPlaceComponent, this.content, 'add-place');
    this.show();
    return this;
  }

  public showEditPlaceModal(place: PlaceApiModel): ModalService {
    this.name = this.translate.instant('places.edit.title');
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.btnSuccessName = this.translate.instant('places.edit.submit');
    this.content = {place};
    this.selectedComponent = new ModalItemModel(AddPlaceComponent, this.content, 'edit-place');
    this.show();
    return this;
  }

  public showVehicleAdvertisementModal(data: {currentUserVM: CurrentUserViewModel, offer?: MarketplaceAdvertisementApiModel}): ModalService {
    const isEdit = !!data?.offer?.['@id'];
    this.name = this.translate.instant(
      isEdit
        ? 'shared.modal.modal_service.update_advertisement.title'
        : 'shared.modal.modal_service.add_advertisement.title'
    );
    this.styles = {
      slideFrom: 'top',
      modalType: 'modal-lg modal-notify modal-info no-padding-modal',
      size: 'modal-fluid',
    };
    this.hasButtons = false;
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddVehicleAdvertisementComponent, this.content, 'add-advertisement');
    this.show();
    return this;
  }

  public showMediaChoiceModal(data: {currentUserVM: CurrentUserViewModel, projectVM?: ProjectViewModel|null}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.choose_media.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-lg modal-notify modal-info no-padding-modal', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(MediaChoiceComponent, this.content, 'add-advertisement');
    this.show();
    return this;
  }

  public showChangeMainOwnerModal(data: {offer: MarketplaceAdvertisementApiModel}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.vehicle_sale.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-sm modal-notify modal-info no-padding-modal', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(ChangeMainOwnerComponent, this.content, 'change-owner');
    this.show();
    return this;
  }

  public showAddDocumentsModal(data: {project: ProjectViewModel, documentAMs: DocumentApiModel[]}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.add_documents.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-sm modal-notify modal-info no-padding-modal', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(AddDocumentsComponent, this.content, 'upsert-documents');
    this.show();
    return this;
  }

  public showReportViolationModal(data: {subject: AbstractApiModel, type: ViolationTypeEnum}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.report_violation.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-sm modal-notify modal-info no-padding-modal', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(ViolationComponent, this.content, 'report-violation');
    this.show();
    return this;
  }

  public showBugReportModal(data: {errorCode?: string | null; errorContext?: BugReportErrorContext | null} = {}): ModalService {
    this.name = this.translate.instant(`shared.modal.modal_service.report_bug.title`);
    this.styles = {slideFrom: 'top', modalType: 'modal-md modal-notify modal-info', size: 'modal-fluid'};
    this.hasButtons = true;
    this.content = data;
    this.selectedComponent = new ModalItemModel(BugReportComponent, this.content, 'report-bug');
    this.show();
    return this;
  }
}
